/**
 * /settings/account — a coach's account settings: name, email and password
 * (edited on their existing pages), her time zone, a link to Settings ›
 * Calendar when she has it, her optional email, and closing the account.
 * Everyone else is sent to the profile settings, as `/settings` does.
 *
 * Aliases: coach settings, account settings page, email preferences page
 */

import { createFileRoute, redirect } from "@tanstack/react-router";

import { CloseAccountCard } from "~/components/close-account-card";
import { TimeZoneCard } from "~/components/time-zone-card";
import {
  coachEmailPreferencesQueryOptions,
  ownTimeZoneQueryOptions,
} from "~/lib/queries/accountSettingsQuery";
import { userProfileQueryOptions } from "~/lib/queries/userQuery";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { hasCoachAccountSettings } from "../-coach-account-settings";

import { AccountSummaryCard } from "./-components/account-summary-card";
import { CalendarLinkCard } from "./-components/calendar-link-card";
import { EmailPreferencesCard } from "./-components/email-preferences-card";

export const Route = createFileRoute("/_user/settings/account/")({
  staticData: { title: "Account" },
  beforeLoad: async ({ context }) => {
    if (
      !(await hasCoachAccountSettings(
        context.queryClient,
        context.user.clerkUserId,
      ))
    ) {
      throw redirect({ to: "/settings/profile" });
    }
  },
  head: () =>
    createNoIndexMeta(
      "Account settings – Dream Coach Match",
      "Your account, time zone and email preferences.",
    ),
  // Hands SSR the page's data; each card reads its own query and shows its
  // own failure, so a failed read here never fails the page.
  loader: async ({ context }) => {
    const { clerkUserId } = context.user;
    await Promise.all([
      context.queryClient
        .ensureQueryData(userProfileQueryOptions(clerkUserId))
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(ownTimeZoneQueryOptions())
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(coachEmailPreferencesQueryOptions())
        .catch(() => undefined),
    ]);
    return { clerkUserId };
  },
  component: AccountSettingsPage,
});

function AccountSettingsPage(): React.JSX.Element {
  const { clerkUserId } = Route.useLoaderData();
  return (
    <div className="max-w-4xl space-y-4">
      <AccountSummaryCard userId={clerkUserId} />
      <TimeZoneCard />
      <CalendarLinkCard />
      <EmailPreferencesCard />
      <CloseAccountCard title="Close account" userId={clerkUserId} />
    </div>
  );
}
